/**
 * §7.3 Enterprise Licensing Plans — 3 licensing cards with Best For +
 * Includes lists.  Anchor id 'pricing-plans' so the hero CTA scrolls
 * directly here.
 *
 * Phase 8 · 07/2026 — CTA buttons now open the <WlCheckoutModal/> for
 * inline discount-code + Stripe Checkout.  The legacy /get-started
 * lead-gen route is preserved as the fallback for the ContactSales
 * banner but no longer intercepts commercial buyers.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import { Check, Sparkles, TrendingUp } from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading, GradientButton, GhostButton,
} from '@/components/landing/design-system'
import { PLANS } from '../data'
import WlCheckoutModal from '../WlCheckoutModal'
import { WL_PLAN_BY_ID } from '@/lib/pricing'

export default function Plans() {
  const [checkoutPlan, setCheckoutPlan] = useState(null)

  const openCheckout = (p) => {
    // Enrich the marketing-data plan with the live catalogue price so
    // the modal knows amount_cents (source of truth = /lib/pricing.js).
    const live = WL_PLAN_BY_ID[p.planId]
    setCheckoutPlan({
      planId:       p.planId,
      label:        p.label,
      duration:     p.duration,
      amount_cents: live?.amount_cents ?? 0,
    })
  }

  return (
    <section id="pricing-plans" className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Enterprise Licensing Plans</Eyebrow>
          <SectionHeading align="center"
            title={<>Three licences.{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One complete platform.</span></>}
            subtitle="Every licence includes the full Kalki Ai Cloud stack. Choose the subscription duration that matches your business goals — upgrade any time."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 pt-4">
          {PLANS.map((p) => (
            <div key={p.id}
              className={clsx(
                'relative rounded-3xl border p-6 md:p-7 backdrop-blur-xl flex flex-col gap-5 transition-all',
                p.highlight
                  ? 'border-brand-gold/40 bg-gradient-to-b from-brand-gold/[0.08] to-white/[0.01] shadow-[0_20px_70px_-20px_rgba(255,193,7,0.35)]'
                  : p.bestValue
                    ? 'border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.05] to-white/[0.01]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.03]',
              )}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 rounded-full border border-brand-gold/40 bg-brand-gold/20 backdrop-blur-md px-3 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-brand-gold whitespace-nowrap shadow-[0_4px_18px_-4px_rgba(255,193,7,0.6)]">
                  <Sparkles className="h-3 w-3" /> Most Popular
                </span>
              )}
              {p.bestValue && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/20 backdrop-blur-md px-3 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-400 whitespace-nowrap shadow-[0_4px_18px_-4px_rgba(52,211,153,0.6)]">
                  <TrendingUp className="h-3 w-3" /> Best Value
                </span>
              )}
              {p.highlight && (
                <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl opacity-70"
                  style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.12), transparent 70%)' }} />
              )}

              <div className="relative flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-white uppercase tracking-wider">{p.label}</span>
                  <span className="text-[10.5px] font-mono text-zinc-500">{p.duration}</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl md:text-5xl font-semibold tracking-tight text-white">{p.price}</span>
                  <span className="text-[13px] text-zinc-500">{p.period}</span>
                </div>
                <div className="text-[12px] text-zinc-500">{p.currency}</div>
              </div>

              {/* Best for */}
              <div className="relative flex flex-col gap-2">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Best for</div>
                <ul className="flex flex-col gap-1.5">
                  {p.bestFor.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[12.5px] text-zinc-300">
                      <span className="mt-1 h-1 w-1 rounded-full bg-zinc-500 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Includes */}
              <div className="relative flex flex-col gap-2">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Includes</div>
                <ul className="flex flex-col gap-1.5">
                  {p.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-2 text-[13px] text-zinc-200">
                      <Check className={clsx('h-3.5 w-3.5 mt-0.5 shrink-0',
                        p.bestValue ? 'text-emerald-400' : 'text-brand-gold',
                      )} />
                      <span>{inc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative mt-auto">
                {p.highlight ? (
                  <GradientButton onClick={() => openCheckout(p)} size="lg" data-testid={`checkout-button-${p.planId}`} className="w-full justify-center">
                    {p.cta}
                  </GradientButton>
                ) : (
                  <GhostButton onClick={() => openCheckout(p)} size="lg" trailingIcon data-testid={`checkout-button-${p.planId}`} className="w-full justify-center">
                    {p.cta}
                  </GhostButton>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> billed via Stripe
          </span>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Every licence includes the complete Kalki Ai Cloud platform.
            Higher tiers differentiate through service level, not feature access.
          </p>
        </div>
      </Container>

      <WlCheckoutModal
        open={!!checkoutPlan}
        plan={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
      />
    </section>
  )
}
